import React, { useEffect, useMemo, useState } from 'react';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { Button } from '@planne-software/uni/Button';

import { useTranslate } from '@src/core/hooks';
import {
  isCartSummaryPresentation,
  isOrderSummaryPresentation,
  isSaleChangeSummaryPresentation,
  isSaleSummaryPresentation,
} from '@src/domains/transactables';
import { SummaryDetails, SummaryItemsList, useCartSummary } from '@src/features/cart';
import { CartCouponApply } from '@src/features/cart/coupon-apply';
import { useOrderSummary } from '@src/features/orders';
import { SaleChangeSummaryDetails, useSaleChangeSummary } from '@src/features/sale-change/summary';
import { useSaleSummary } from '@src/features/sales/summary';
import { useCompactSummary, useTransactableSummary } from '@src/features/transactables/summary';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';

import { useCheckoutCouponManageability } from './hooks/useCheckoutCouponManageability';
import { usePurchaseSummary } from './hooks/usePurchaseSummary';
import {
  MobileSidebarBar,
  MobileSidebarContent,
  MobileSidebarOverlay,
  MobileSidebarDivider,
  MobileSidebarHeader,
  MobileSidebarProducts,
  MobileSidebarSummary,
  MobileSidebarTop,
  MobileSidebarTotal,
} from './styles';

export const CheckoutCompactSidebar = () => {
  const { i18n } = useTranslate();
  const { source, transactable, cart, isReady, canManageCartCoupon } = useTransactableSummary();
  const canManageCoupon = useCheckoutCouponManageability(canManageCartCoupon);
  const { order, sale, saleChange } = transactable;
  const cartSummary = useCartSummary(cart);
  const orderSummary = useOrderSummary(order);
  const saleSummary = useSaleSummary(sale);
  const saleChangeSummary = useSaleChangeSummary(saleChange, sale, order);
  const { summary: purchaseSummary } = usePurchaseSummary();
  const { compactSummary, compactItems } = useCompactSummary({
    source,
    transactable,
    cart,
    cartSummary,
    orderSummary,
    saleSummary,
    saleChangeSummary,
    purchaseSummary,
  });
  const { formatCents } = useCurrencyFormat();
  const [summaryOpen, setSummaryOpen] = useState(false);

  useEffect(() => {
    if (!summaryOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [summaryOpen]);

  const summaryDetails = useMemo(() => {
    if (!summaryOpen) {
      return null;
    }

    if (isCartSummaryPresentation(source, transactable)) {
      return (
        <SummaryDetails
          summary={cartSummary}
          canManageCoupon={canManageCoupon}
          data-testid='checkout-mobile-summary-details'
        />
      );
    }

    if (isSaleChangeSummaryPresentation(source)) {
      return <SaleChangeSummaryDetails summary={saleChangeSummary} />;
    }

    if (isOrderSummaryPresentation(source)) {
      return <SummaryDetails summary={orderSummary} data-testid='checkout-mobile-order-summary-details' />;
    }

    if (isSaleSummaryPresentation(source, transactable)) {
      return <SummaryDetails summary={saleSummary} data-testid='checkout-mobile-sale-summary-details' />;
    }

    return null;
  }, [summaryOpen, source, transactable, cartSummary, orderSummary, saleSummary, saleChangeSummary, canManageCoupon]);

  if (!isReady) {
    return null;
  }

  const totalLabel = i18n('checkout_cart_summary_total', {
    total: formatCents(compactSummary.totalAmountCents),
  });
  const productsLabel = i18n('checkout_cart_summary_products', { count: compactSummary.productCount });

  return (
    <>
      {summaryOpen ? (
        <MobileSidebarOverlay data-testid='checkout-mobile-sidebar-overlay' onClick={() => setSummaryOpen(false)} />
      ) : null}

      <MobileSidebarBar data-testid='checkout-mobile-sidebar'>
        <MobileSidebarTop>
          <MobileSidebarHeader>
            <MobileSidebarSummary>
              <MobileSidebarTotal>{totalLabel}</MobileSidebarTotal>
              <MobileSidebarProducts>{productsLabel}</MobileSidebarProducts>
            </MobileSidebarSummary>

            <Button
              type='button'
              variant='outlined'
              size='sm'
              aria-expanded={summaryOpen}
              endSlot={summaryOpen ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
              onClick={() => setSummaryOpen((open) => !open)}
            >
              {i18n(summaryOpen ? 'checkout_hide_summary' : 'checkout_view_summary')}
            </Button>
          </MobileSidebarHeader>

          {summaryDetails}
        </MobileSidebarTop>

        {summaryOpen ? <MobileSidebarDivider data-testid='checkout-mobile-summary-divider' /> : null}

        <MobileSidebarContent open={summaryOpen}>
          {summaryOpen && canManageCoupon ? <CartCouponApply /> : null}
          {summaryOpen ? <SummaryItemsList items={compactItems} /> : null}
        </MobileSidebarContent>
      </MobileSidebarBar>
    </>
  );
};

export default CheckoutCompactSidebar;
